import React, { useState, useEffect } from 'react';
import { 
  Package, Check, Calendar, CreditCard, FileText, 
  Loader2, ChevronRight, Clock, CheckCircle
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientPackageSummary({ clientId }) {
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (clientId) {
      loadPackageSummary();
    }
  }, [clientId]);

  const loadPackageSummary = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      const res = await axios.get(
        `${API_URL}/api/contracted-packages/client-portal/${clientId}/package-summary`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPackages(res.data.packages || []);
    } catch (err) {
      console.error('Error loading package summary:', err);
      setError('No se pudo cargar la información del paquete');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-[#e03c3c]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center text-[#AEAEB2]">
        <Package className="w-12 h-12 mx-auto mb-3 text-[#6E6E73]" />
        <p>{error}</p>
      </div>
    );
  }

  if (!packages.length) {
    return (
      <div className="p-6 text-center text-[#AEAEB2]">
        <Package className="w-12 h-12 mx-auto mb-3 text-[#6E6E73]" />
        <p>No hay paquetes contratados</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {packages.map((pkg, index) => (
        <div key={index} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#e03c3c] to-[#f47c2a] p-6 text-white">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Package className="w-6 h-6" />
                  {pkg.package_name || 'Mi Paquete'}
                </h2>
                <p className="text-[#1D1D1F]/80 text-sm mt-1">{pkg.business_name}</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold">${pkg.monthly_total?.toLocaleString()}</p>
                <p className="text-[#1D1D1F]/80 text-sm">MXN / mes</p>
              </div>
            </div>
            
            {/* Status badges */}
            <div className="flex gap-3 mt-4">
              {pkg.contract_date && (
                <span className="px-3 py-1 bg-white/20 rounded-full text-xs flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Desde {new Date(pkg.contract_date).toLocaleDateString('es-MX', { month: 'short', year: 'numeric' })}
                </span>
              )}
              {pkg.payment_status === 'paid' && (
                <span className="px-3 py-1 bg-green-500/30 rounded-full text-xs flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Al corriente
                </span>
              )}
              {pkg.payment_status === 'pending' && (
                <span className="px-3 py-1 bg-yellow-500/30 rounded-full text-xs flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Pendiente
                </span>
              )}
            </div>
          </div>
          
          {/* Services List */}
          <div className="p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#e03c3c]" />
              Servicios Incluidos
            </h3>
            
            <div className="space-y-4">
              {pkg.services?.map((service, sIndex) => (
                <div key={sIndex} className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        {service.name}
                        {service.quantity > 1 && (
                          <span className="ml-2 text-sm font-normal text-[#AEAEB2]">
                            x{service.quantity}
                          </span>
                        )}
                      </h4>
                      {service.description && (
                        <p className="text-sm text-[#AEAEB2]">{service.description}</p>
                      )}
                    </div>
                    <span className="text-[#e03c3c] font-semibold">
                      ${service.subtotal?.toLocaleString()}
                    </span>
                  </div>
                  
                  {/* What's included */}
                  {service.includes?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs font-medium text-[#AEAEB2] mb-2">Incluye:</p>
                      <ul className="grid md:grid-cols-2 gap-1">
                        {service.includes.map((item, iIndex) => (
                          <li key={iIndex} className="text-xs text-[#6E6E73] flex items-start gap-1">
                            <Check className="w-3 h-3 text-green-500 flex-shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Completion Progress (if available) */}
            {pkg.current_month_completion !== null && pkg.current_month_completion !== undefined && (
              <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-800">Avance del mes actual</span>
                  <span className="text-sm font-bold text-blue-900">{pkg.current_month_completion}%</span>
                </div>
                <div className="h-2 bg-blue-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-600 rounded-full transition-all duration-500"
                    style={{ width: `${pkg.current_month_completion}%` }}
                  />
                </div>
              </div>
            )}
            
            {/* Next Payment Info */}
            {pkg.next_billing_date && (
              <div className="mt-6 p-4 bg-orange-50 rounded-xl flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-orange-800">Próximo pago</p>
                    <p className="text-lg font-bold text-orange-900">
                      {new Date(pkg.next_billing_date).toLocaleDateString('es-MX', { 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-orange-400" />
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
